import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, downloadFile } from '../../utils/api';
import '../Experiments.css';

const LEVEL_LABEL = { error: '오류', warn: '확인', info: '안내' };
const LEVEL_ORDER = { error: 0, warn: 1, info: 2 };

function UploadFormRestore() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [version, setVersion] = useState('');
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    apiGet('/api/companies/')
      .then(list => {
        const rows = Array.isArray(list) ? list : (list?.companies || []);
        setCompanies(rows);
        if (rows.length && !companyId) setCompanyId(rows[0]._id || rows[0].id);
      })
      .catch(e => setError(e.message));
    // 최초 1회만 회사 목록을 불러온다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const company = useMemo(
    () => companies.find(c => (c._id || c.id) === companyId),
    [companies, companyId]
  );

  const query = () => {
    const params = new URLSearchParams({ company_id: companyId, year, month });
    if (version) params.set('version', version);
    return params.toString();
  };

  const handlePreflight = async () => {
    setError('');
    setReport(null);
    setBusy('preflight');
    try {
      setReport(await apiGet(`/api/experiments/upload-form/restore/preflight?${query()}`));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const handleDownload = async () => {
    setError('');
    setBusy('download');
    try {
      const name = `${company?.name || '회사'}_${year}-${String(month).padStart(2, '0')}_복원양식.xlsx`;
      await downloadFile(`/api/experiments/upload-form/restore/download?${query()}`, name);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const issues = (report?.issues || [])
    .slice()
    .sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level]);
  const counts = report?.counts || {};

  return (
    <div className="experiments-container">
      <div className="restore-notice">
        <strong>수식은 살아 있지만 계산 결과는 비어 있습니다.</strong>
        <span>
          내려받은 파일을 <b>엑셀에서 한 번 열어 저장한 뒤</b> 사용하세요.
          열지 않고 그대로 다시 업로드하면 계산 컬럼이 빈 값으로 인식됩니다.
        </span>
      </div>

      <div className="experiments-section">
        <h3>대상 선택</h3>
        <div className="restore-form">
          <label>
            회사
            <select value={companyId} onChange={e => setCompanyId(e.target.value)}>
              {companies.map(c => (
                <option key={c._id || c.id} value={c._id || c.id}>{c.name}</option>
              ))}
            </select>
          </label>
          <label>
            년
            <input type="number" value={year} onChange={e => setYear(Number(e.target.value))} />
          </label>
          <label>
            월
            <input type="number" min="1" max="12" value={month}
                   onChange={e => setMonth(Number(e.target.value))} />
          </label>
          <label>
            버전
            <input type="number" placeholder="현재" value={version}
                   onChange={e => setVersion(e.target.value)} />
          </label>
          <button type="button" className="experiments-btn-secondary"
                  disabled={!companyId || !!busy} onClick={handlePreflight}>
            {busy === 'preflight' ? '확인 중…' : '복원 가능 여부 확인'}
          </button>
          <button type="button" className="experiments-btn-primary"
                  disabled={!companyId || !!busy} onClick={handleDownload}>
            {busy === 'download' ? '만드는 중…' : '양식으로 내려받기'}
          </button>
        </div>
        {error && <div className="restore-error">{error}</div>}
      </div>

      {report && (
        <div className="experiments-section-full">
          <h3>
            복원 리포트
            <span className="restore-counts">
              오류 {counts.error || 0} · 확인 {counts.warn || 0} · 안내 {counts.info || 0}
            </span>
          </h3>

          <table className="restore-sheet-table">
            <thead>
              <tr><th>시트</th><th>처리</th></tr>
            </thead>
            <tbody>
              {Object.entries(report.sheets || {}).map(([name, info]) => (
                <tr key={name}>
                  <td>{name}</td>
                  <td>
                    {info.rows != null && `${info.rows}명 `}
                    {info.mode === 'values_only' && '· 값만 복원(수식 없음) '}
                    {info.written != null && `· 기록 ${info.written}칸 `}
                    {info.appended_cols ? `· 컬럼 ${info.appended_cols}개 추가 ` : ''}
                    {info.formula_cleared ? `· 수식 ${info.formula_cleared}칸 지움` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <table className="restore-issue-table">
            <thead>
              <tr><th>구분</th><th>내용</th></tr>
            </thead>
            <tbody>
              {issues.map((issue, i) => (
                <tr key={i} className={`restore-issue-${issue.level}`}>
                  <td>{LEVEL_LABEL[issue.level]}</td>
                  <td>{issue.message}</td>
                </tr>
              ))}
              {!issues.length && (
                <tr><td colSpan={2}>특이사항 없이 전부 되돌릴 수 있습니다.</td></tr>
              )}
            </tbody>
          </table>
          <p className="restore-hint">
            같은 내용이 내려받은 파일의 <b>사용자노트</b> 시트(맨 앞)에도 들어 있습니다.
          </p>
        </div>
      )}
    </div>
  );
}

export default UploadFormRestore;
